import { Component } from '@theme/component';
import {
  StandardEvents,
  CartLinesUpdateEvent,
} from '@shopify/events';
import {
  DrawerOpenEvent,
  DrawerCloseEvent,
} from '@theme/theme-drawer';

/**
 * AMLAYA Archetype-style cart drawer.
 *
 * Handles:
 * - automatic drawer opening
 * - chronological cart add history
 * - Tops <-> Bottoms complementary recommendations
 * - automatic fallback when the latest product is removed
 * - instant recommendation switching
 * - Archetype drawer upsell Add
 * - delivery popover behavior
 * - drawer scroll locking
 */
class CartDrawerComponent extends Component {
  /** @type {MutationObserver | null} */
  #relatedProductsObserver = null;

  /** @type {number} */
  #historyCounter = 0;

  /**
   * Add operations that have fired but whose Shopify
   * requests have not yet settled.
   *
   * @type {Set<string>}
   */
  #pendingAddTokens = new Set();

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#themeDrawer?.addEventListener(
      DrawerCloseEvent.eventName,
      this.#handleDrawerClose
    );

    this.addEventListener(
      'click',
      this.#handleClick
    );

    /*
     * Seed our chronological history from products
     * already in the cart when the page loads.
     */
    this.#seedHistoryFromCurrentCart();

    /*
     * Horizon replaces/hydrates the cart drawer after
     * AJAX mutations. Re-apply the recommendation after
     * its DOM has changed.
     */
    this.#relatedProductsObserver =
      new MutationObserver(() => {
        queueMicrotask(() => {
          this.#updateRelatedProducts();
        });
      });

    this.#relatedProductsObserver.observe(
      this,
      {
        childList: true,
        subtree: true,
      }
    );

    requestAnimationFrame(() => {
      this.#updateRelatedProducts();
    });

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#themeDrawer?.removeEventListener(
      DrawerCloseEvent.eventName,
      this.#handleDrawerClose
    );

    this.removeEventListener(
      'click',
      this.#handleClick
    );

    this.#relatedProductsObserver?.disconnect();
    this.#relatedProductsObserver = null;
  }

  #handleDrawerOpen = () => {
    document.documentElement.classList.add(
      'cw-cart-wishlist-open'
    );

    this.#updateRelatedProducts();
  };

  #handleDrawerClose = () => {
    document.documentElement.classList.remove(
      'cw-cart-wishlist-open'
    );

    this.#closeDeliveryPopovers();
  };

  /**
   * Handles every cart lines mutation.
   *
   * Add:
   *   immediately push the variant onto history.
   *
   * Remove:
   *   after Shopify confirms success, remove variants
   *   that no longer exist in the cart from history.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const addedProduct =
      this.#getLatestComplementaryAdd(event);

    let addToken = '';

    /*
     * ADD
     *
     * Add the new product to history immediately rather
     * than waiting for Shopify.
     *
     * This makes recommendations flip instantly.
     */
    if (addedProduct) {
      addToken = this.#createHistoryToken();

      this.#pendingAddTokens.add(
        addToken
      );

      this.#appendHistoryEntry(
        addedProduct.variantId,
        addToken
      );

      this.#applyRelatedGroup(
        addedProduct.group
      );
    }

    /*
     * Because cart-drawer-component now always carries
     * [auto-open], every successful Add to Cart should
     * open the drawer.
     */
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    /*
     * Quick Add may be inside a modal dialog.
     * Let that modal close before opening the drawer.
     */
    const sourceModal =
      event.target instanceof Element
        ? /** @type {HTMLDialogElement | null} */ (
            event.target.closest('dialog:modal')
          )
        : null;

    /*
     * For normal page Add to Cart we can open immediately.
     *
     * When the cart was empty, the success path below
     * will open it after Horizon fills the cart.
     */
    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        /*
         * Shopify rejected this Add.
         *
         * Remove only THIS specific history entry.
         * This is important when several Add requests
         * happen very quickly.
         */
        if (detail?.didError) {
          if (addToken) {
            this.#pendingAddTokens.delete(
              addToken
            );

            this.#removeHistoryEntryByToken(
              addToken
            );
          }

          this.#updateRelatedProducts();
          return;
        }

        /*
         * Successful Add.
         *
         * The history entry was already added in user
         * action order, so simply mark this request settled.
         */
        if (addToken) {
          this.#pendingAddTokens.delete(
            addToken
          );
        }

        /*
         * Successful REMOVE.
         *
         * detail.items is Horizon's complete new cart.
         *
         * Removing C therefore deletes C from history:
         *
         * [A, B, C] -> [A, B]
         *
         * and B automatically becomes latest again.
         */
        if (event.action === 'remove') {
          this.#reconcileHistoryWithCartItems(
            detail?.items
          );
        }

        /*
         * If another cart mechanism changes the cart,
         * reconcile on ordinary updates as well.
         */
        if (event.action === 'update') {
          this.#reconcileHistoryWithCartItems(
            detail?.items
          );
        }

        /*
         * Update now and again after Horizon's section
         * hydration paint.
         */
        this.#updateRelatedProducts();

        requestAnimationFrame(() => {
          this.#updateRelatedProducts();
        });

        if (!shouldAutoOpen) {
          return;
        }

        const openDrawer = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          this.#updateRelatedProducts();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openDrawer,
            { once: true }
          );
        } else {
          openDrawer();
        }
      })
      .catch((error) => {
        /*
         * Network failure on an Add.
         *
         * Remove that operation's optimistic entry but
         * don't disturb any later additions.
         */
        if (addToken) {
          this.#pendingAddTokens.delete(
            addToken
          );

          this.#removeHistoryEntryByToken(
            addToken
          );

          this.#updateRelatedProducts();
        }

        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Cart event rejected:',
            error
          );
        }
      });
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const addButton =
      event.target.closest(
        '[data-cw-add-variant]'
      );

    if (
      addButton instanceof HTMLButtonElement
    ) {
      event.preventDefault();
      event.stopPropagation();

      const variantId =
        addButton.getAttribute(
          'data-cw-add-variant'
        );

      if (variantId) {
        this.#addVariant(
          variantId,
          addButton
        );
      }

      return;
    }

    const infoButton =
      event.target.closest(
        '[data-cw-delivery-toggle]'
      );

    if (infoButton instanceof HTMLElement) {
      event.preventDefault();

      /*
       * Desktop uses the Archetype hover treatment.
       * Click/tap is only necessary on touch devices.
       */
      if (
        window.matchMedia(
          '(hover: hover) and (pointer: fine)'
        ).matches
      ) {
        return;
      }

      const wrapper =
        infoButton.closest(
          '.cw-info-wrap'
        );

      const popover =
        wrapper?.querySelector(
          '[data-cw-delivery-popover]'
        );

      if (
        popover instanceof HTMLElement
      ) {
        const shouldOpen =
          popover.hidden;

        this.#closeDeliveryPopovers(
          popover
        );

        popover.hidden =
          !shouldOpen;

        infoButton.setAttribute(
          'aria-expanded',
          shouldOpen ? 'true' : 'false'
        );
      }

      return;
    }

    if (
      !event.target.closest(
        '.cw-info-wrap'
      )
    ) {
      this.#closeDeliveryPopovers();
    }
  };

  /* ======================================================
     RELATED PRODUCT HISTORY
     ====================================================== */

  /**
   * History structure:
   *
   * [
   *   { variantId: "A", token: "..." },
   *   { variantId: "B", token: "..." },
   *   { variantId: "C", token: "..." }
   * ]
   *
   * Last valid item is the latest added product.
   */
  #getHistory() {
    try {
      const raw =
        sessionStorage.getItem(
          'amlaya-cart-add-history'
        );

      if (!raw) {
        return [];
      }

      const parsed =
        JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter(
        (entry) =>
          entry &&
          typeof entry.variantId === 'string' &&
          typeof entry.token === 'string'
      );
    } catch {
      return [];
    }
  }

  #saveHistory(history) {
    try {
      sessionStorage.setItem(
        'amlaya-cart-add-history',
        JSON.stringify(history)
      );
    } catch {
      // Ignore restricted storage environments.
    }
  }

  #createHistoryToken() {
    this.#historyCounter += 1;

    return [
      Date.now(),
      this.#historyCounter,
      Math.random()
        .toString(36)
        .slice(2, 8),
    ].join('-');
  }

  /**
   * Seed history from the cart that exists when the
   * current page first loads.
   *
   * Liquid renders these oldest -> newest.
   */
  #seedHistoryFromCurrentCart() {
    const registry =
      this.querySelector(
        '[data-cart-related-registry]'
      );

    if (!registry) {
      return;
    }

    const currentVariants =
      Array.from(
        registry.querySelectorAll(
          '[data-cart-existing-variant]'
        )
      )
        .map((element) =>
          element.getAttribute(
            'data-cart-existing-variant'
          )
        )
        .filter(Boolean);

    /*
     * Empty cart.
     */
    if (currentVariants.length === 0) {
      this.#saveHistory([]);

      try {
        sessionStorage.removeItem(
          'amlaya-cart-last-added-variant'
        );

        sessionStorage.removeItem(
          'amlaya-cart-related-group'
        );
      } catch {
        // Ignore.
      }

      return;
    }

    const currentSet =
      new Set(currentVariants);

    let history =
      this.#getHistory().filter(
        (entry) =>
          currentSet.has(entry.variantId)
      );

    const historyVariantSet =
      new Set(
        history.map(
          (entry) => entry.variantId
        )
      );

    /*
     * Products already in the cart but absent from our
     * browser history are treated as older than tracked
     * additions.
     */
    const missingEntries =
      currentVariants
        .filter(
          (variantId) =>
            !historyVariantSet.has(
              variantId
            )
        )
        .map(
          (variantId, index) => ({
            variantId,
            token:
              `seed-${Date.now()}-${index}-${variantId}`,
          })
        );

    history = [
      ...missingEntries,
      ...history,
    ];

    /*
     * Migrate the old one-value implementation if this
     * visitor still has it in sessionStorage.
     */
    try {
      const legacyVariant =
        sessionStorage.getItem(
          'amlaya-cart-last-added-variant'
        );

      if (
        legacyVariant &&
        currentSet.has(legacyVariant)
      ) {
        history.push({
          variantId:
            legacyVariant,

          token:
            `legacy-${Date.now()}-${legacyVariant}`,
        });
      }

      sessionStorage.removeItem(
        'amlaya-cart-last-added-variant'
      );

      sessionStorage.removeItem(
        'amlaya-cart-related-group'
      );
    } catch {
      // Ignore.
    }

    this.#saveHistory(history);
  }

  #appendHistoryEntry(
    variantId,
    token
  ) {
    if (!variantId || !token) {
      return;
    }

    const history =
      this.#getHistory();

    history.push({
      variantId:
        String(variantId),

      token:
        String(token),
    });

    this.#saveHistory(history);
  }

  #removeHistoryEntryByToken(
    token
  ) {
    if (!token) {
      return;
    }

    const history =
      this.#getHistory().filter(
        (entry) =>
          entry.token !== token
      );

    this.#saveHistory(history);
  }

  /**
   * Remove history entries for products that no longer
   * exist in Shopify's real cart.
   *
   * Pending Adds are preserved because their network
   * requests may not have settled yet.
   */
  #reconcileHistoryWithCartItems(
    items
  ) {
    if (!Array.isArray(items)) {
      return;
    }

    const currentVariants =
      items
        .map((item) =>
          String(
            item?.variant_id ??
            item?.id ??
            ''
          )
        )
        .filter(Boolean);

    const currentSet =
      new Set(currentVariants);

    let history =
      this.#getHistory().filter(
        (entry) =>
          currentSet.has(
            entry.variantId
          ) ||
          this.#pendingAddTokens.has(
            entry.token
          )
      );

    /*
     * If some product entered the cart through a mechanism
     * we didn't observe, keep it as an older fallback
     * instead of losing it completely.
     */
    const historyVariantSet =
      new Set(
        history.map(
          (entry) => entry.variantId
        )
      );

    const missingEntries =
      currentVariants
        .filter(
          (variantId) =>
            !historyVariantSet.has(
              variantId
            )
        )
        .map(
          (variantId, index) => ({
            variantId,

            token:
              `sync-${Date.now()}-${index}-${variantId}`,
          })
        );

    history = [
      ...missingEntries,
      ...history,
    ];

    this.#saveHistory(history);
  }

  /**
   * Finds the newest product in the add event that belongs
   * to Tops or Bottoms.
   */
  #getLatestComplementaryAdd(
    event
  ) {
    if (
      event.action !== 'add' ||
      !Array.isArray(event.lines) ||
      event.lines.length === 0
    ) {
      return null;
    }

    const addedLines =
      [...event.lines].reverse();

    for (const line of addedLines) {
      const variantId =
        String(
          line?.merchandiseId ||
          ''
        );

      if (!variantId) {
        continue;
      }

      const group =
        this.#getComplementaryGroupForVariant(
          variantId
        );

      if (!group) {
        continue;
      }

      return {
        variantId,
        group,
      };
    }

    return null;
  }

  /**
   * Top -> show Bottoms
   * Bottom -> show Tops
   */
  #getComplementaryGroupForVariant(
    variantId
  ) {
    const registry =
      this.querySelector(
        '[data-cart-related-registry]'
      );

    if (!registry || !variantId) {
      return '';
    }

    if (
      registry.querySelector(
        `[data-cart-top-variant="${variantId}"]`
      )
    ) {
      return 'bottoms';
    }

    if (
      registry.querySelector(
        `[data-cart-bottom-variant="${variantId}"]`
      )
    ) {
      return 'tops';
    }

    return '';
  }

  /**
   * Walk backwards through history until we find the
   * newest product that belongs to one of our two groups.
   */
  #getCurrentRelatedGroup() {
    const history =
      this.#getHistory();

    for (
      let index =
        history.length - 1;
      index >= 0;
      index -= 1
    ) {
      const group =
        this.#getComplementaryGroupForVariant(
          history[index].variantId
        );

      if (group) {
        return group;
      }
    }

    /*
     * Last-resort Liquid fallback.
     */
    const registry =
      this.querySelector(
        '[data-cart-related-registry]'
      );

    const fallback =
      registry?.dataset
        .defaultRelatedGroup;

    if (
      fallback === 'tops' ||
      fallback === 'bottoms'
    ) {
      return fallback;
    }

    return 'bottoms';
  }

  #applyRelatedGroup(group) {
    if (
      group !== 'tops' &&
      group !== 'bottoms'
    ) {
      return;
    }

    const section =
      this.querySelector(
        '[data-cw-related-products]'
      );

    if (!section) {
      return;
    }

    this.querySelectorAll(
      '[data-related-group]'
    ).forEach((track) => {
      track.hidden =
        track.dataset
          .relatedGroup !== group;
    });

    this.querySelectorAll(
      '[data-related-copy]'
    ).forEach((copy) => {
      copy.hidden =
        copy.dataset
          .relatedCopy !== group;
    });

    const activeTrack =
      this.querySelector(
        `[data-related-group="${group}"]`
      );

    section.hidden =
      !activeTrack ||
      !activeTrack.querySelector(
        '.cw-upsell-card'
      );
  }

  #updateRelatedProducts() {
    this.#applyRelatedGroup(
      this.#getCurrentRelatedGroup()
    );
  }

  /* ======================================================
     DRAWER UPSELL ADD
     ====================================================== */

  async #addVariant(
    variantId,
    sourceButton
  ) {
    sourceButton.setAttribute(
      'aria-busy',
      'true'
    );

    const deferredEvent =
      CartLinesUpdateEvent.createPromise();

    /*
     * Dispatch the same event Horizon's normal product
     * forms use.
     *
     * Therefore adding C through this upsell becomes the
     * latest history item immediately.
     */
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',

        lines: [
          {
            merchandiseId:
              String(variantId),

            quantity: 1,
          },
        ],

        promise:
          deferredEvent.promise,
      })
    );

    try {
      const root =
        window.Shopify?.routes
          ?.root || '/';

      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',
            },

            credentials:
              'same-origin',

            body: JSON.stringify({
              items: [
                {
                  id:
                    Number(variantId),

                  quantity:
                    1,
                },
              ],

              sections: [
                'cart-drawer-section',
              ],

              sections_url:
                window.location
                  .pathname,
            }),
          }
        );

      const payload =
        await response.json();

      if (!response.ok) {
        throw new Error(
          payload.description ||
          payload.message ||
          'Unable to add this product.'
        );
      }

      const cartResponse =
        await fetch(
          `${root}cart.js`,
          {
            headers: {
              Accept:
                'application/json',
            },

            credentials:
              'same-origin',
          }
        );

      if (!cartResponse.ok) {
        throw new Error(
          'Unable to refresh cart.'
        );
      }

      const cart =
        await cartResponse.json();

      deferredEvent.resolve({
        cart:
          CartLinesUpdateEvent
            .createCartFromAjaxResponse(
              cart
            ),

        detail: {
          sections:
            payload.sections,

          items:
            cart.items,

          itemCount:
            cart.item_count,

          source:
            'cart-drawer-upsell',

          didError:
            false,
        },
      });

      requestAnimationFrame(() => {
        this.#updateRelatedProducts();
      });
    } catch (error) {
      console.error(
        '[cart-drawer] Upsell add failed:',
        error
      );

      deferredEvent.reject(error);
    } finally {
      sourceButton.removeAttribute(
        'aria-busy'
      );
    }
  }

  /* ======================================================
     DELIVERY POPOVER
     ====================================================== */

  #closeDeliveryPopovers(
    except = null
  ) {
    this.querySelectorAll(
      '[data-cw-delivery-popover]'
    ).forEach((popover) => {
      if (popover === except) {
        return;
      }

      popover.hidden = true;

      const button =
        popover.parentElement
          ?.querySelector(
            '[data-cw-delivery-toggle]'
          );

      button?.setAttribute(
        'aria-expanded',
        'false'
      );
    });
  }

  #isCartEmpty() {
    return Boolean(
      this.querySelector(
        '.cart-drawer--empty'
      )
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}